if (!customElements.get('product-media-carousel')) {
  class ProductMediaCarousel extends HTMLElement {
    constructor() {
      super();
      this.thumbnailSlider = null;
      this.mainSlider = null;

      this.updateMousewheel = this.updateMousewheel.bind(this);
      this.normalizeHeight = this.normalizeHeight.bind(this);
      this.applyMode = this.applyMode.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this.onVariantSelected = this.onVariantSelected.bind(this);
      this.onMainSliderChange = this.onMainSliderChange.bind(this);
      this.onThumbnailKeydown = this.onThumbnailKeydown.bind(this);

      this.resizeTO = null;
    }

    connectedCallback() {
      if (typeof Swiper === 'undefined') {
        const checkSwiper = setInterval(() => {
          if (typeof Swiper !== 'undefined') {
            clearInterval(checkSwiper);
            this.initDomRefs();
            this.applyMode();
            this.initZoom();
            window.addEventListener('resize', this.onWindowResize);
          }
        }, 100);
        setTimeout(() => clearInterval(checkSwiper), 5000);
      } else {
        this.initDomRefs();
        this.applyMode();
        this.initZoom();
        window.addEventListener('resize', this.onWindowResize);
      }

      // Listen for variant changes to sync media
      document.addEventListener('variant:selected', this.onVariantSelected);
      this.addEventListener('keydown', this.onThumbnailKeydown);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:load', () => {
          this.initDomRefs();
          this.applyMode();
          this.initZoom();
        });
        document.addEventListener('shopify:section:unload', () => this.destroyAll());
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onWindowResize);
      document.removeEventListener('variant:selected', this.onVariantSelected);
      this.removeEventListener('keydown', this.onThumbnailKeydown);
      this.destroyAll();
    }

    initDomRefs() {
      this.mainContainer = this.querySelector('.product-media-carousel__main .swiper');
      this.thumbsContainer = this.querySelector('.product-media-carousel__thumbnails .swiper');
      this.thumbsWrap = this.querySelector('.product-media-carousel__thumbnails');
      this.nextBtn = this.querySelector('.swiper-button-next');
      this.prevBtn = this.querySelector('.swiper-button-prev');
      this.mediaCount = Number(this.dataset.mediaCount || 0);
      this.desktopPref = this.dataset.sliderDesktop || 'slider';
      this.mobilePref = this.dataset.sliderMobile || 'slider';
      this.sliderPosition = (this.dataset.sliderPosition || '').toLowerCase();
    }

    isMobileWidth() { return window.innerWidth < 990; }
    isTouchLike() { return window.matchMedia('(pointer: coarse)').matches; }

    getViewportMode() {
      const pref = this.isMobileWidth() ? this.mobilePref : this.desktopPref;
      if (this.mediaCount < 2) return 'single';
      if (pref === 'grid') return 'grid';
      if (pref === 'single') return 'single';
      return 'slider';
    }

    onWindowResize() {
      clearTimeout(this.resizeTO);
      this.resizeTO = setTimeout(this.applyMode, 150);
    }

    applyMode() {
      const mode = this.getViewportMode();
      this.setAttribute('data-mode', mode);
    
      if (mode === 'slider') {
        if (!this.mainSlider) {
          this.initSliders();
        } else {
          this.updateMousewheel(this.mainSlider);
          this.normalizeHeight(this.mainSlider);
          this.pauseInactiveSlideMedia(this.mainSlider.activeIndex);
          this.mainSlider.update();
        }
        if (this.thumbsWrap) {
          this.thumbsWrap.hidden = false;
          this.thumbsWrap.setAttribute('aria-hidden', 'false');
        }
      } else {
        this.destroyAll();
        this.normalizeHeight();
        if (this.thumbsWrap) {
          this.thumbsWrap.hidden = true;
          this.thumbsWrap.setAttribute('aria-hidden', 'true');
        }
      }
    }

    initSliders() {
      if (!this.mainContainer) return;

      if (this.thumbsContainer && this.mediaCount > 1) {
        this.thumbnailSlider = new Swiper(this.thumbsContainer, {
          spaceBetween: 8,
          slidesPerView: 5,
          freeMode: false,
          watchSlidesProgress: true,
          centeredSlides: false,
          allowTouchMove: true,
          loop: false,
          loopFillGroupWithBlank: false,
          navigation: {
            nextEl: this.nextBtn,
            prevEl: this.prevBtn,
          },
          breakpoints: {
            240: {
              slidesPerView: 5,
              spaceBetween: 8,
            },
            750: {
              slidesPerView: 5,
              spaceBetween: 8,
            },
            990: {
              slidesPerView: 5,
              spaceBetween: 8,
            }
          },
          on: {
            init: (swiper) => {
              // Delay to ensure DOM is ready
              setTimeout(() => {
                this.updateThumbnailNavigation(swiper);
                this.updateThumbnailAccessibility();
              }, 100);
            },
            slideChange: (swiper) => {
              this.updateThumbnailNavigation(swiper);
              this.updateThumbnailAccessibility();
            },
            reachEnd: (swiper) => {
              this.updateThumbnailNavigation(swiper);
              this.updateThumbnailAccessibility();
            },
            reachBeginning: (swiper) => {
              this.updateThumbnailNavigation(swiper);
              this.updateThumbnailAccessibility();
            },
            resize: (swiper) => {
              this.updateThumbnailNavigation(swiper);
              this.updateThumbnailAccessibility();
            }
          }
        });
      }

      const isCover = this.sliderPosition === 'cover';

      this.mainSlider = new Swiper(this.mainContainer, {
        spaceBetween: 2,
        speed: 600,

        mousewheel: true,
        keyboard: { enabled: true },

        navigation: {
          nextEl: this.nextBtn,
          prevEl: this.prevBtn,
        },
        thumbs: this.thumbnailSlider ? { swiper: this.thumbnailSlider } : null,

        observer: true,
        observeParents: true,
        observeSlideChildren: true,

        breakpoints: {
          240: { 
            slidesPerView: 1, 
            autoHeight: true 
          },
          990: { 
            slidesPerView: 'auto', 
            autoHeight: isCover ? false : true
          }
        },

        on: {
          init: (s) => {
            this.updateMousewheel(s);
            this.normalizeHeight(s);
            this.pauseInactiveSlideMedia(s.activeIndex);
          },
          slideChange: this.onMainSliderChange,
          breakpoint: (s) => {
            this.updateMousewheel(s);
            this.normalizeHeight(s);
            this.pauseInactiveSlideMedia(s.activeIndex);
          },
          resize: (s) => {
            this.updateMousewheel(s);
            this.normalizeHeight(s);
            this.pauseInactiveSlideMedia(s.activeIndex);
          },
          imagesReady: (s) => {
            this.normalizeHeight(s);
          }
        }
      });
    }

    onMainSliderChange(swiper) {
      this.pauseInactiveSlideMedia(swiper.activeIndex);
      this.updateThumbnailAccessibility();
    }

    onThumbnailKeydown(event) {
      const isActivationKey = event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
      if (!isActivationKey) return;

      const thumbnail = event.target.closest('.product-media-carousel__thumbnails .product-media-carousel__item');
      if (!thumbnail || !this.contains(thumbnail)) return;

      event.preventDefault();

      const mediaId = thumbnail.dataset.mediaId;
      if (!mediaId) return;

      this.setActiveMedia(mediaId);
    }

    pauseInactiveSlideMedia(activeIndex) {
      const slides = this.querySelectorAll('.product-media-carousel__main .swiper-slide');
      if (!slides.length) return;

      slides.forEach((slide, index) => {
        if (index === activeIndex) return;
        this.pauseMediaInContainer(slide);
      });
    }

    pauseMediaInContainer(container) {
      if (!container) return;

      container.querySelectorAll('video').forEach((video) => {
        if (!video.paused) {
          video.pause();
        }
      });

      container.querySelectorAll('model-viewer').forEach((modelViewer) => {
        if (typeof modelViewer.pause === 'function') {
          modelViewer.pause();
        }
      });

      container.querySelectorAll('iframe').forEach((iframe) => {
        try {
          if (
            iframe.src.includes('youtube.com')
            || iframe.src.includes('youtu.be')
            || iframe.src.includes('youtube-nocookie.com')
          ) {
            iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          } else if (iframe.src.includes('vimeo.com')) {
            iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
          }
        } catch (error) {
          // Ignore cross-origin player messaging errors.
        }
      });
    }

    updateMousewheel(swiper) {
      const s = swiper || this.mainSlider;
      if (!s) return;
      if (this.isTouchLike() || this.isMobileWidth()) {
        if (s.mousewheel && s.mousewheel.enabled) s.mousewheel.disable();
      } else {
        if (s.mousewheel && !s.mousewheel.enabled) s.mousewheel.enable();
      }
    }

    normalizeHeight(swiper) {
      const s = swiper || this.mainSlider;
      const desktop = !this.isMobileWidth();

      requestAnimationFrame(() => {
        const el = s ? s.el : this.mainContainer;
        const wrapper = s ? s.wrapperEl : this.querySelector('.product-media-carousel__main .swiper-wrapper');
        if (wrapper) wrapper.style.removeProperty('height');
        if (el) el.style.removeProperty('height');

        if (s) {
          try {
            if (desktop) {
              // Check if swiper is initialized before calling update methods
              if (typeof s.updateSize === 'function') s.updateSize();
              if (typeof s.updateSlides === 'function') s.updateSlides();
              if (typeof s.updateProgress === 'function') s.updateProgress();
              if (typeof s.updateSlidesClasses === 'function') s.updateSlidesClasses();
              if (typeof s.slideTo === 'function' && typeof s.activeIndex !== 'undefined') {
                s.slideTo(s.activeIndex, 0, false);
              }
            } else if (s.params && s.params.autoHeight && typeof s.updateAutoHeight === 'function') {
              s.updateAutoHeight(0);
            }
          } catch (error) {
            console.warn('Error updating swiper height:', error);
          }
        }
      });
    }

    /**
     * Handle variant selection to sync media gallery
     */
    onVariantSelected(event) {
      const { featuredMediaId, sectionId, html } = event.detail || {};

      const productInfo = this.closest('product-info');
      const currentSectionId = productInfo?.dataset.section;

      if (currentSectionId && sectionId && currentSectionId !== sectionId) {
        return;
      }

      if (!featuredMediaId) {
        return;
      }

      if (this.isHideVariantsEnabled() && html) {
        this.syncGalleryFromVariantHtml(html, sectionId);
      }

      this.setActiveMedia(featuredMediaId);
    }

    /**
     * Update thumbnail navigation buttons state
     */
    updateThumbnailNavigation(swiper) {
      if (!swiper || !this.nextBtn || !this.prevBtn) return;
      
      const { isBeginning, isEnd, activeIndex, slides } = swiper;
      
      // Safety check for slides
      if (!slides || !Array.isArray(slides)) {
        this.nextBtn.classList.add('swiper-button-disabled');
        this.nextBtn.setAttribute('aria-disabled', 'true');
        this.nextBtn.disabled = true;
        this.prevBtn.classList.add('swiper-button-disabled');
        this.prevBtn.setAttribute('aria-disabled', 'true');
        this.prevBtn.disabled = true;
        return;
      }

      const updateButtonState = (button, shouldDisable) => {
        button.classList.toggle('swiper-button-disabled', shouldDisable);
        button.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
        button.disabled = shouldDisable;
      };

      const totalSlides = slides.length;
      const slidesPerViewValue = Number(swiper.params?.slidesPerView);
      const slidesPerView = Number.isFinite(slidesPerViewValue) && slidesPerViewValue > 0
        ? Math.floor(slidesPerViewValue)
        : 1;
      const hasOverflow = totalSlides > slidesPerView;
      const lastNavigableIndex = Math.max(0, totalSlides - slidesPerView);

      const disablePrev = !hasOverflow || isBeginning || activeIndex <= 0;
      const disableNext = !hasOverflow || isEnd || activeIndex >= lastNavigableIndex;

      updateButtonState(this.prevBtn, disablePrev);
      updateButtonState(this.nextBtn, disableNext);
    }

    updateThumbnailAccessibility() {
      const thumbnails = this.querySelectorAll('.product-media-carousel__thumbnails .product-media-carousel__item');
      if (!thumbnails.length) return;

      thumbnails.forEach((thumbnail) => {
        const isActive = thumbnail.classList.contains('swiper-slide-thumb-active');
        thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    /**
     * Set active media by ID (scroll to slide with featured media)
     */
    setActiveMedia(mediaId) {
      if (!mediaId) return;

      const mode = this.getViewportMode();

      if (mode !== 'slider') {
        this.moveMediaToFirstPosition(String(mediaId));
        return;
      }

      // Only navigate in slider mode
      if (!this.mainSlider) {
        return;
      }

      // Find slide with matching media ID
      const slides = this.querySelectorAll('.product-media-carousel__main .swiper-slide');
      let targetIndex = -1;


      slides.forEach((slide, index) => {
        const slideMediaId = slide.dataset.mediaId;
        if (slideMediaId === String(mediaId)) {
          targetIndex = index;
        }
      });

      // Navigate to the slide if found
      if (targetIndex >= 0 && this.mainSlider) {
        this.mainSlider.slideTo(targetIndex, 600);
        this.updateThumbnailAccessibility();
      } else {
        console.warn(`[Media gallery] Slide not found for media ID: ${mediaId}`);
      }
    }

    moveMediaToFirstPosition(mediaId) {
      if (!mediaId) return;

      const wrappers = [
        '.product-media-carousel__main .swiper-wrapper',
        '.product-media-carousel__thumbnails .swiper-wrapper',
        '.zoom-dialog__media-list'
      ];

      wrappers.forEach((selector) => {
        const wrapper = this.querySelector(selector);
        if (!wrapper) return;

        const targetMedia = wrapper.querySelector(`[data-media-id="${mediaId}"]`);
        if (!targetMedia || wrapper.firstElementChild === targetMedia) return;

        wrapper.insertBefore(targetMedia, wrapper.firstElementChild);
      });

      this.updateZoomIndexes();
    }

    isHideVariantsEnabled() {
      return this.dataset.hideVariants === 'true';
    }

    syncGalleryFromVariantHtml(html, sectionId) {
      if (!html) return;

      const updatedGallery = html.querySelector(`#MainProductMedia-${sectionId} product-media-carousel`);
      if (!updatedGallery) return;

      this.destroyAll();
      this.innerHTML = updatedGallery.innerHTML;

      this.dataset.sliderDesktop = updatedGallery.dataset.sliderDesktop || this.dataset.sliderDesktop || 'slider';
      this.dataset.sliderMobile = updatedGallery.dataset.sliderMobile || this.dataset.sliderMobile || 'slider';
      this.dataset.sliderPosition = updatedGallery.dataset.sliderPosition || this.dataset.sliderPosition || '';
      this.dataset.mediaCount = updatedGallery.dataset.mediaCount || this.dataset.mediaCount || '0';
      this.dataset.hideVariants = updatedGallery.dataset.hideVariants || this.dataset.hideVariants || 'false';

      this.initDomRefs();
      this.applyMode();
      this.initZoom();
    }

    updateZoomIndexes() {
      const zoomTriggers = this.querySelectorAll(
        '.product-media-carousel__main .product-media-carousel__image--zoomable[data-zoom-trigger]'
      );

      zoomTriggers.forEach((trigger, index) => {
        trigger.dataset.zoomIndex = String(index);
      });
    }

    /**
     * Initialize zoom functionality
     */
    initZoom() {
      const zoomDialog = this.querySelector('zoom-dialog');
      if (!zoomDialog) return;

      const zoomTriggers = this.querySelectorAll('[data-zoom-trigger]');
      zoomTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          let index = parseInt(trigger.dataset.zoomIndex, 10) || 0;

          const triggerSlide = trigger.closest('.product-media-carousel__item');
          const mediaId = triggerSlide?.dataset.mediaId;
          if (mediaId) {
            const zoomItems = Array.from(zoomDialog.querySelectorAll('.zoom-dialog__media-item'));
            const matchedIndex = zoomItems.findIndex((item) => item.dataset.mediaId === mediaId);
            if (matchedIndex >= 0) {
              index = matchedIndex;
            }
          }

          if (typeof zoomDialog.open === 'function') {
            zoomDialog.open(index);
          }
        });
      });
    }

    destroyAll() {
      this.querySelectorAll('.product-media-carousel__main .swiper-slide, .zoom-dialog__media-item').forEach((container) => {
        this.pauseMediaInContainer(container);
      });

      if (this.mainSlider) {
        this.mainSlider.destroy(true, true);
        this.mainSlider = null;
      }
      if (this.thumbnailSlider) {
        this.thumbnailSlider.destroy(true, true);
        this.thumbnailSlider = null;
      }
    }
  }

  customElements.define('product-media-carousel', ProductMediaCarousel);
}